import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { ChatSideContent } from '../chatSideContent.types';
import {
  ChatSideContentContext,
  ChatSideContentContextValue,
} from './chatSideContent.context';

interface ChatSideContentProviderProps {
  /**
   * Identity of the scope open content belongs to — e.g. the active
   * conversation. When it changes, any open content closes: side content is
   * scoped to where it was opened from.
   */
  scopeKey?: string;
}

/**
 * Holds the state of the chat side content pane: a single content slot, so
 * opening new content (another citation, a document summary) replaces what's
 * currently shown rather than stacking.
 */
export function ChatSideContentProvider({
  children,
  scopeKey,
}: PropsWithChildren<ChatSideContentProviderProps>) {
  const [openState, setOpenState] = useState<{
    content: ChatSideContent;
    key: number;
  } | null>(null);

  useUpdateEffect(() => {
    setOpenState(null);
  }, [scopeKey]);

  const openSideContent = useCallback((content: ChatSideContent) => {
    setOpenState((prev) => ({ content, key: (prev?.key ?? 0) + 1 }));
  }, []);

  const closeSideContent = useCallback(() => {
    setOpenState(null);
  }, []);

  const value = useMemo<ChatSideContentContextValue>(
    () => ({
      sideContent: openState?.content ?? null,
      sideContentKey: openState?.key ?? 0,
      openSideContent,
      closeSideContent,
    }),
    [openState, openSideContent, closeSideContent]
  );

  return (
    <ChatSideContentContext.Provider value={value}>
      {children}
    </ChatSideContentContext.Provider>
  );
}
